import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { 
  Button, 
  Card, 
  Checkbox, 
  FormControlLabel, 
  TextField, 
  Typography,
  Container,
  Alert,
  CircularProgress
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UnauthNav from "../../components/UnauthNav/UnauthNav";
import UserApi from "../../api/UserApi";
import "./FinishUserAccountView.scss";

const FinishUserAccountView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // URL parameters
  const payload = searchParams.get('token') || searchParams.get('payload') || '';
  const email = searchParams.get('email') || '';
  const userGuid = searchParams.get('guid') || searchParams.get('User_guid') || '';

  // Form state
  const [password, setPassword] = useState<string>('');
  const [passwordConf, setPasswordConf] = useState<string>('');
  const [dob, setDob] = useState<string>('');
  const [tos, setTos] = useState<boolean>(false);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  // Validation functions
  const passwordValid = (): boolean => {
    const lowercaseRegex = /[a-z]/;
    const uppercaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[@.#$!%^&*.?]/;
    return (
      password.length > 0 &&
      lowercaseRegex.test(password) &&
      uppercaseRegex.test(password) &&
      numberRegex.test(password) &&
      specialCharRegex.test(password) &&
      password.length >= 8 &&
      password.length <= 20
    );
  };

  const passwordConfValid = (): boolean => {
    return passwordConf.length > 0 && password === passwordConf;
  };

  const dobValid = (): boolean => {
    return dob.length > 0 && !isNaN(Date.parse(dob)) && Date.parse(dob) < Date.now();
  };

  const isFormValid = (): boolean => {
    return passwordValid() && passwordConfValid() && dobValid() && tos && !!payload && !!userGuid;
  };

  // Check if required URL parameters are present
  useEffect(() => {
    if (!payload || !userGuid) {
      setError('Invalid or missing account creation link parameters. Please check your email for the correct link.');
    }
  }, [payload, userGuid]);

  const handleFinishAccountCreation = async (): Promise<void> => {
    if (!isFormValid()) return;

    setLoading(true);
    setError('');

    try {
      await UserApi.finishAccountCreation({
        User_guid: userGuid,
        User_Password: password,
        User_DOB: new Date(dob).toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        User_TermsOfService: tos,
        payload: payload
      });

      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Account creation failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !loading && isFormValid()) {
      handleFinishAccountCreation();
    }
  };

  // Success state
  if (success) {
    return (
      <>
        <UnauthNav />
        <div className="finish-account">
          <Card className="finish-account-card">
            <PersonAddIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" className="finish-account-card-title">
              Account Created Successfully!
            </Typography>
            <Typography variant="body1" className="finish-account-card-subtitle">
              Your account has been set up and you can now log in.
            </Typography>
            <Button 
              component={Link} 
              to="/login" 
              variant="contained" 
              color="primary"
              size="large"
              fullWidth
            >
              Go to Login
            </Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <UnauthNav />
      <div className="finish-account">
        <Card className="finish-account-card">
          <PersonAddIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" className="finish-account-card-title">
            Finish Account Setup
          </Typography>
          <Typography variant="body2" className="finish-account-card-subtitle">
            {email && `Welcome ${email}! `}Please create a password, provide your date of birth, and accept the terms of service to complete your account setup.
          </Typography>

          {(!payload || !userGuid) && (
            <Alert severity="error" sx={{ width: '100%' }}>
              Invalid account creation link. Please check your email for the correct link.
            </Alert>
          )}

          <TextField
            disabled={loading || !payload || !userGuid}
            value={password}
            error={password.length > 0 && !passwordValid()}
            helperText={
              password.length > 0 && !passwordValid() 
                ? "Password must be 8-20 characters with uppercase, lowercase, number, and special character" 
                : ""
            }
            required
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            label="New Password"
            variant="outlined"
            type="password"
            autoFocus
          />

          <TextField
            disabled={loading || !payload || !userGuid}
            value={passwordConf}
            error={passwordConf.length > 0 && !passwordConfValid()}
            helperText={
              passwordConf.length > 0 && !passwordConfValid() 
                ? "Passwords do not match" 
                : ""
            }
            required
            onChange={(e) => setPasswordConf(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Confirm Password"
            variant="outlined"
            type="password"
          />

          <TextField
            disabled={loading || !payload || !userGuid}
            value={dob}
            error={dob.length > 0 && !dobValid()}
            helperText={
              dob.length > 0 && !dobValid() 
                ? "Please enter a valid date of birth" 
                : ""
            }
            required
            onChange={(e) => setDob(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Date of Birth"
            variant="outlined"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControlLabel
            disabled={loading || !payload || !userGuid}
            control={
              <Checkbox
                checked={tos}
                onChange={(e) => setTos(e.target.checked)}
                color="primary"
              />
            }
            label={
              <span>
                I agree to the <a href="https://txticketdr.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              </span>
            }
          />

          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          <Button 
            disabled={!isFormValid() || loading} 
            onClick={handleFinishAccountCreation} 
            variant="contained" 
            color="primary"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
          >
            {loading ? "Setting up account..." : "Complete Account Setup"}
          </Button>

          <hr />

          <Button 
            disabled={loading} 
            component={Link} 
            to="/login" 
            variant="outlined" 
            color="inherit" 
            className="finish-account-card-back-btn"
            startIcon={<ArrowBackIcon />}
          >
            Back to Login
          </Button>
        </Card>
      </div>
    </>
  );
};

export default FinishUserAccountView;
